import { nanoid } from '@reduxjs/toolkit';
import {
  BY_METRIC_LINE_WIDTH,
  ChartType,
  DATE_COLUMN_GROUP,
  DEFAULT_LEFT_AXIS_SERIES,
  DEFAULT_LINE_WIDTH,
  DEFAULT_RIGHT_AXIS_SERIES,
  DEFAULT_SPLIT_SORT,
  NO_AXIS_SELECTED_VALUE,
  SplitSortBy,
  getRequestedMetrics,
  resolveSortMetric,
} from '@/features/chartsView';
import type { AxisSeriesSettings, SplitSort } from '@/features/chartsView';
import type {
  Filters,
  NumberFilterParams,
  StringFilterParams,
} from '@/features/filters';
import { getApplicableFilters, prepareFilters } from '@/features/filters';
import { prepareDateRangeValueObject } from '@/shared/api';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import type {
  DateRangeLimits,
  SerializedDateRangeValueObject,
} from '@/shared/types';
import { DateRangePresets, DateScale, Lifetime } from '@/shared/types';
import * as CohortHistoryChartAPI from '../api/cohortHistoryChartAPI';
import type { GetDateFilterParams } from '../api/types';
import { DEFAULT_PRIMARY_METRIC, DEFAULT_SPLIT_DIMENSION } from './constants';
import type {
  CohortHistoryChartConfig,
  CohortHistoryChartDataResponse,
  ChartMeta,
} from './types';

export interface State {
  meta:
    | {
        data: ChartMeta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  chart: CohortHistoryChartConfig;
  isConfigInitialized: boolean;
  splitSortBySession: Record<string, SplitSort>;
}

const PINNED_SETTINGS = {
  cohortDateScale: DateScale.DAY,
  lifetime: Lifetime.YEAR,
} as const;

const initialState: State = {
  meta: {
    data: null,
    status: DataStatus.idle,
  },
  isConfigInitialized: false,
  splitSortBySession: {},
  chart: {
    id: '',
    type: ChartType.cohortHistory,
    snapshotDateScale: DateScale.DAY,
    snapshotDateRange: { preset: DateRangePresets.LAST_90_DAYS },
    cohortDateRange: { preset: DateRangePresets.ALL_TIME },
    probabilisticAttribution: true,
    splitDimension: '',
    primaryMetric: '',
    secondaryMetric: NO_AXIS_SELECTED_VALUE,
    excludedSplitOptions: [],
    syncedYAxes: false,
    highlightLineOnHover: true,
    showOnlyFullCohorts: true,
    filters: [],
    leftAxisSeries: { ...DEFAULT_LEFT_AXIS_SERIES },
    rightAxisSeries: { ...DEFAULT_RIGHT_AXIS_SERIES },
    splitSort: { ...DEFAULT_SPLIT_SORT },
    ...PINNED_SETTINGS,
  },
};

// Not `isDateDimension` (label formatting): for `cohort_ww` they disagree on purpose.
export const isChronologicalSplit = (
  meta: ChartMeta | null,
  dimension: string,
): boolean =>
  meta?.config[dimension]?.group?.toLowerCase() === DATE_COLUMN_GROUP;

export const computeDefaultSplitSort = (
  meta: ChartMeta | null,
  dimension: string,
): SplitSort => ({
  by: isChronologicalSplit(meta, dimension)
    ? SplitSortBy.name
    : SplitSortBy.value,
  metric: '',
  direction: DEFAULT_SPLIT_SORT.direction,
});

const resolveAgainstCatalog = (
  preferred: string,
  catalog: string[],
  fallback: string,
) => {
  return catalog.includes(preferred) ? preferred : fallback;
};

const buildVendorDefault = (meta: ChartMeta): CohortHistoryChartConfig => {
  const splitDimension = resolveAgainstCatalog(
    DEFAULT_SPLIT_DIMENSION,
    meta.dimensions,
    meta.defaultAxis.dimension,
  );

  return {
    id: nanoid(),
    type: ChartType.cohortHistory,
    excludedSplitOptions: [],
    syncedYAxes: false,
    highlightLineOnHover: true,
    showOnlyFullCohorts: true,
    filters: [],
    snapshotDateScale: DateScale.DAY,
    snapshotDateRange: { preset: DateRangePresets.LAST_90_DAYS },
    cohortDateRange: { preset: DateRangePresets.ALL_TIME },
    probabilisticAttribution: true,
    splitDimension,
    primaryMetric: resolveAgainstCatalog(
      DEFAULT_PRIMARY_METRIC,
      meta.metrics,
      meta.defaultAxis.primaryMetric,
    ),
    secondaryMetric: NO_AXIS_SELECTED_VALUE,
    leftAxisSeries: { ...DEFAULT_LEFT_AXIS_SERIES },
    rightAxisSeries: { ...DEFAULT_RIGHT_AXIS_SERIES },
    splitSort: computeDefaultSplitSort(meta, splitDimension),
    ...PINNED_SETTINGS,
  };
};

const healAxisSeries = (
  series: AxisSeriesSettings | undefined,
  catalogMetrics: Set<string>,
  fallback: AxisSeriesSettings,
): AxisSeriesSettings => {
  const current = series ?? { ...fallback };

  if (current.lineWidthMetric && catalogMetrics.has(current.lineWidthMetric)) {
    return current;
  }

  if (!current.lineWidthMetric && current.lineWidth !== BY_METRIC_LINE_WIDTH) {
    return current;
  }

  return {
    ...current,
    lineWidthMetric: '',
    lineWidth:
      current.lineWidth === BY_METRIC_LINE_WIDTH
        ? DEFAULT_LINE_WIDTH
        : current.lineWidth,
  };
};

const getHealedAxes = (
  chart: CohortHistoryChartConfig,
  catalogMetrics: Set<string>,
) => {
  return {
    leftAxisSeries: healAxisSeries(
      chart.leftAxisSeries,
      catalogMetrics,
      DEFAULT_LEFT_AXIS_SERIES,
    ),
    rightAxisSeries: healAxisSeries(
      chart.rightAxisSeries,
      catalogMetrics,
      DEFAULT_RIGHT_AXIS_SERIES,
    ),
  };
};

const rememberSplitSort = (state: State) => {
  if (!state.chart?.splitSort) {
    return;
  }

  state.splitSortBySession[state.chart.splitDimension] = state.chart.splitSort;
};

const healSplitSort = (
  chart: CohortHistoryChartConfig,
  meta: ChartMeta,
): SplitSort => {
  const current =
    chart.splitSort ?? computeDefaultSplitSort(meta, chart.splitDimension);

  const healed =
    current.metric && !meta.metrics.includes(current.metric)
      ? { ...current, metric: '' }
      : current;

  if (
    healed.by === SplitSortBy.value &&
    !resolveSortMetric({ ...chart, splitSort: healed })
  ) {
    return { ...healed, by: SplitSortBy.name };
  }

  return healed;
};

const healAxesAgainstMeta = (state: State) => {
  if (!isMetaFinished(state.meta)) {
    return;
  }

  // Built fresh: incoming configs may be Immer-frozen, so mutating in place would throw.
  const healed = {
    ...state.chart,
    ...getHealedAxes(state.chart, new Set(state.meta.data.metrics)),
  };

  state.chart = {
    ...healed,
    splitSort: healSplitSort(healed, state.meta.data),
  };

  rememberSplitSort(state);
};

export const cohortHistoryChartSlice = createAppSlice({
  name: 'cohortHistoryChart',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return CohortHistoryChartAPI.getMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
          healAxesAgainstMeta(state);
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;
            const { status } = state.cohortHistoryChart.meta;

            if (isLoading(status) || isFinished(status)) {
              return false;
            }
          },
        },
      },
    ),
    updateSnapshotDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.snapshotDateRange = action.payload;
      },
    ),
    updateCohortDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.cohortDateRange = action.payload;
      },
    ),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.probabilisticAttribution = action.payload;
    }),
    updateSplitDimension: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      const next = action.payload;

      state.chart.splitDimension = next;
      state.chart.splitSort =
        state.splitSortBySession[next] ??
        computeDefaultSplitSort(state.meta.data, next);
    }),
    updateSplitSort: create.reducer<Partial<SplitSort>>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.splitSort = {
        ...(state.chart.splitSort ?? DEFAULT_SPLIT_SORT),
        ...action.payload,
      };
      rememberSplitSort(state);
    }),
    resetSplitSort: create.reducer((state) => {
      if (!state.chart) {
        return;
      }

      const dimension = state.chart.splitDimension;

      delete state.splitSortBySession[dimension];
      state.chart.splitSort = computeDefaultSplitSort(
        state.meta.data,
        dimension,
      );
    }),
    updatePrimaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.primaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric]?.type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }

      healAxesAgainstMeta(state);
    }),
    updateSecondaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.secondaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric]?.type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }

      healAxesAgainstMeta(state);
    }),
    updateAxisSeries: create.reducer<{
      axis: 'left' | 'right';
      patch: Partial<AxisSeriesSettings>;
    }>((state, action) => {
      if (!state.chart) {
        return;
      }

      const key =
        action.payload.axis === 'left' ? 'leftAxisSeries' : 'rightAxisSeries';

      state.chart[key] = { ...state.chart[key], ...action.payload.patch };
      healAxesAgainstMeta(state);
    }),
    updateExcludedSplitOptions: create.reducer<string[]>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.excludedSplitOptions = action.payload;
    }),
    updateSyncedYAxes: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.syncedYAxes = action.payload;
    }),
    updateHighlightLineOnHover: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.highlightLineOnHover = action.payload;
    }),
    updateShowOnlyFullCohorts: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.showOnlyFullCohorts = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.filters = action.payload;
    }),
    updateChart: create.reducer<CohortHistoryChartConfig>((state, action) => {
      state.chart = { ...action.payload, ...PINNED_SETTINGS };
      state.isConfigInitialized = true;
      healAxesAgainstMeta(state);
    }),
    setupVendorDefault: create.reducer((state) => {
      if (!isMetaFinished(state.meta)) {
        return;
      }

      state.chart = buildVendorDefault(state.meta.data);
      state.isConfigInitialized = true;
      rememberSplitSort(state);
    }),
    setupVendorDefaultWithOverride: create.reducer<
      Partial<CohortHistoryChartConfig>
    >((state, action) => {
      if (!isMetaFinished(state.meta)) {
        return;
      }

      state.chart = {
        ...buildVendorDefault(state.meta.data),
        ...action.payload,
        ...PINNED_SETTINGS,
      };
      healAxesAgainstMeta(state);
      state.isConfigInitialized = true;
    }),
    setupDefaultExcludedSplitOptions: create.reducer<string[]>(
      (state, action) => {
        const optionsToExclude = action.payload.slice(3);

        state.chart.excludedSplitOptions = optionsToExclude;
      },
    ),
    toggleExcludedSplitOptions: create.reducer<{
      options: string[];
      checked: boolean;
    }>((state, action) => {
      if (!state.chart) {
        return;
      }

      const excludedOptionsSet = new Set(state.chart.excludedSplitOptions);

      if (!action.payload.checked) {
        action.payload.options.forEach((option) => {
          excludedOptionsSet.add(option);
        });
      } else {
        action.payload.options.forEach((option) => {
          excludedOptionsSet.delete(option);
        });
      }

      state.chart.excludedSplitOptions = Array.from(excludedOptionsSet);
    }),
  }),
  selectors: {
    selectChart: (state) => state.chart,
    selectIsConfigInitialized: (state) => state.isConfigInitialized,
    selectMeta: (state) => state.meta.data,
    selectMetaDataStatus: (state) => state.meta.status,
    selectMetaIsLoading: (state) => isLoading(state.meta.status),
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
  },
});

function isMetaFinished(meta: State['meta']): meta is {
  data: ChartMeta;
  status: DataStatus.finished;
} {
  return meta.status === DataStatus.finished;
}

export const getChartData =
  (
    params: CohortHistoryChartConfig,
  ): AppThunk<Promise<CohortHistoryChartDataResponse>> =>
  (_dispatch, getState) => {
    const state = getState() as RootState;

    const { meta } = state.cohortHistoryChart;

    if (!isMetaFinished(meta)) {
      return Promise.reject(new Error('Meta not initialized'));
    }

    const { dimensionsFilters, metricsFilters } = prepareFilters(
      params.filters,
      meta.data.metrics,
      meta.data.dimensions,
    );

    return CohortHistoryChartAPI.getCohortHistoryChartData({
      split: params.splitDimension,
      metrics: getRequestedMetrics(params),
      snapshotDateScale: params.snapshotDateScale,
      snapshotDateRange: prepareDateRangeValueObject(params.snapshotDateRange),
      // Not from params: tiles render a stored preset directly, skipping the re-pinning reducers.
      cohortDateScale: PINNED_SETTINGS.cohortDateScale,
      cohortDateRange: prepareDateRangeValueObject(params.cohortDateRange),
      lifetime: PINNED_SETTINGS.lifetime,
      probabilisticAttribution: params.probabilisticAttribution,
      dimensionsFilters,
      metricsFilters,
    });
  };

export const getDateFilterParams =
  (params: GetDateFilterParams): AppThunk<Promise<DateRangeLimits>> =>
  () => {
    return CohortHistoryChartAPI.getDateFilterParams(params).then((dto) => ({
      min: new Date(dto.min),
      max: new Date(dto.max),
    }));
  };

interface GetStringFilterParamsThunkParams {
  filterName: string;
  filters: Filters;
  allMetrics: string[];
  allDimensions: string[];
  snapshotDateRange: SerializedDateRangeValueObject;
  snapshotDateScale: DateScale;
  cohortDateRange: SerializedDateRangeValueObject;
  cohortDateScale: DateScale;
  probabilisticAttribution: boolean;
}

export const getStringFilterParams =
  (
    params: GetStringFilterParamsThunkParams,
  ): AppThunk<Promise<StringFilterParams>> =>
  () => {
    const { dimensionsFilters } = prepareFilters(
      getApplicableFilters(params.filters, params.filterName),
      params.allMetrics,
      params.allDimensions,
    );

    return CohortHistoryChartAPI.getStringFilterParams({
      filterName: params.filterName,
      dimensionsFilters,
      snapshotDateRange: prepareDateRangeValueObject(params.snapshotDateRange),
      snapshotDateScale: params.snapshotDateScale,
      cohortDateRange: prepareDateRangeValueObject(params.cohortDateRange),
      cohortDateScale: params.cohortDateScale,
      probabilisticAttribution: params.probabilisticAttribution,
    });
  };

interface GetNumberFilterParamsThunkParams {
  filterName: string;
  split: string;
  filters: Filters;
  allMetrics: string[];
  allDimensions: string[];
  snapshotDateRange: SerializedDateRangeValueObject;
  snapshotDateScale: DateScale;
  cohortDateRange: SerializedDateRangeValueObject;
  cohortDateScale: DateScale;
  lifetime: Lifetime;
  probabilisticAttribution: boolean;
}

export const getNumberFilterParams =
  (
    params: GetNumberFilterParamsThunkParams,
  ): AppThunk<Promise<NumberFilterParams>> =>
  () => {
    const { dimensionsFilters, metricsFilters } = prepareFilters(
      getApplicableFilters(params.filters, params.filterName),
      params.allMetrics,
      params.allDimensions,
    );

    return CohortHistoryChartAPI.getNumberFilterParams({
      filterName: params.filterName,
      split: params.split,
      dimensionsFilters,
      metricsFilters,
      snapshotDateRange: prepareDateRangeValueObject(params.snapshotDateRange),
      snapshotDateScale: params.snapshotDateScale,
      cohortDateRange: prepareDateRangeValueObject(params.cohortDateRange),
      cohortDateScale: params.cohortDateScale,
      lifetime: params.lifetime,
      probabilisticAttribution: params.probabilisticAttribution,
    });
  };

export const {
  initMeta,
  updateSnapshotDateRange,
  updateCohortDateRange,
  updateProbabilisticAttribution,
  updateSplitDimension,
  updateSplitSort,
  resetSplitSort,
  updatePrimaryMetric,
  updateSecondaryMetric,
  updateExcludedSplitOptions,
  updateSyncedYAxes,
  updateHighlightLineOnHover,
  updateShowOnlyFullCohorts,
  updateAxisSeries,
  updateFilters,
  updateChart,
  setupVendorDefault,
  setupVendorDefaultWithOverride,
  toggleExcludedSplitOptions,
  setupDefaultExcludedSplitOptions,
} = cohortHistoryChartSlice.actions;
export const {
  selectChart,
  selectIsConfigInitialized,
  selectMeta,
  selectMetaDataStatus,
  selectMetaIsLoaded,
  selectMetaIsLoading,
} = cohortHistoryChartSlice.selectors;
